import { useEffect, useRef, useState } from "react";
import { CountUp } from "@/components/motion/CountUp";
import { api } from "@/lib/api";
import { gsap, MOTION_OK, useGSAP } from "@/lib/gsap";
import type { InquiryStatsDTO } from "@/types/inquiry";

/**
 * 견적 섹션 바로 위에 까는 접수 현황 띠. 계산기로 들어가기 직전에 "지금도 오고 있다"를 한 줄로 전해요.
 *
 * 실패하면 아무것도 안 그려요 — `lib/track.ts` 와 같은 원칙으로, 곁다리 요소가 사이트를 깨뜨리면 안 돼요.
 * 둘 다 0 이면 숨겨요: 초기에 "오늘 0건" 은 사회적 증거가 아니라 역효과예요.
 */
export function InquiryStats() {
  const [stats, setStats] = useState<InquiryStatsDTO | null>(null);
  const dotRef = useRef<HTMLSpanElement>(null);

  // 살아 있는 숫자라는 신호. 느리게 숨 쉬듯 뛰어요. 숫자가 도착한 뒤에야 점이 생겨서 stats 에 매요.
  useGSAP(
    () => {
      const dot = dotRef.current;
      if (!dot) return;
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        gsap.to(dot, { scale: 1.6, opacity: 0.35, duration: 1.1, ease: "sine.inOut", repeat: -1, yoyo: true });
      });
    },
    { dependencies: [stats !== null] },
  );

  useEffect(() => {
    let alive = true;
    api
      .get<InquiryStatsDTO>("/inquiry-stats")
      .then((data) => {
        if (alive) setStats(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (stats === null || (stats.today === 0 && stats.month === 0)) return null;

  return (
    <aside className="border-line-alternative border-y bg-background-alternative px-6 py-6">
      {/* 모바일에선 항목마다 한 줄씩 — flex-wrap 은 "2개 + 1개" 처럼 애매하게 접혀요.
          점은 첫 줄과 같이 다녀야 해서 첫 문장 안에 넣어요(따로 두면 세로 배치에서 혼자 한 줄을 먹어요). */}
      <div className="mx-auto flex max-w-layout flex-col items-center gap-y-1 text-center sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-4">
        <p className="wds-body-2 text-label-neutral">
          <span
            ref={dotRef}
            aria-hidden="true"
            className="mr-2 inline-block size-2 rounded-full bg-primary align-middle"
          />
          오늘 견적문의{" "}
          <CountUp value={stats.today} className="wds-headline-1 font-bold text-primary" />
          건 접수
        </p>
        <span aria-hidden="true" className="hidden h-3 w-px bg-line-neutral sm:block" />
        <p className="wds-body-2 text-label-neutral">
          이번 달 총{" "}
          <CountUp value={stats.month} className="wds-headline-1 font-bold text-primary" />건
        </p>
        {/* 0 이면 통째로 빼요 — "답변 완료 0건" 은 오늘 0건과 같은 이유로 역효과예요. */}
        {stats.month_done > 0 && (
          <>
            <span aria-hidden="true" className="hidden h-3 w-px bg-line-neutral sm:block" />
            <p className="wds-body-2 text-label-neutral">
              답변 완료{" "}
              <CountUp value={stats.month_done} className="wds-headline-1 font-bold text-primary" />건
            </p>
          </>
        )}
      </div>
    </aside>
  );
}

export default InquiryStats;
